'use client';

import { useCallback } from 'react';
import { useAppSelector } from '../redux/hooks';
import { translations, TranslationKeys } from './translations';

type NestedKeyOf<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? `${K}.${NestedKeyOf<T[K]>}` | K
          : K
        : never;
    }[keyof T]
  : never;

type TranslationPath = NestedKeyOf<TranslationKeys>;

export function useTranslation() {
  const locale = useAppSelector((state) => state.language.locale);
  const t = translations[locale];

  // ``translate`` MUST keep a stable identity across renders for any
  // given locale. Several callers (form pages that load Odoo data via
  // useEffect) include ``t`` in their dependency arrays; if we re-
  // created the function on every render those effects would cancel
  // themselves on every state update, the cleanup would set the local
  // ``cancelled`` flag to true before the in-flight async settled,
  // and the ``finally`` clause would skip ``setLoading(false)`` —
  // leaving the page stuck on the spinner.
  const translate = useCallback(
    (path: TranslationPath): string => {
      const keys = path.split('.');
      let result: unknown = t;

      for (const key of keys) {
        if (result && typeof result === 'object' && key in result) {
          result = (result as Record<string, unknown>)[key];
        } else {
          return path; // Return the path if translation not found
        }
      }

      return typeof result === 'string' ? result : path;
    },
    [t],
  );

  return { t: translate, locale, translations: t };
}

